/**
 * Hover detail viewer — the product page centrepiece.
 *
 * Moving across the garment swaps the pane to the macro shot for that region,
 * translating physical inspection of a garment onto the screen. Regions come
 * from the custom.detail_map metafield; without it the image falls back to
 * horizontal thirds mapped to images 2–4.
 *
 * Standard and touch get the identical macros as a tappable strip — the
 * mechanism changes, the content does not.
 */

/* Inlined rather than imported — see the note at the top of mode.js. */
const getMode = () =>
  document.documentElement.classList.contains('mode-immersive') ? 'immersive' : 'standard';
const canBeImmersive = () =>
  window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initDetailViewer(scope = document) {
  const viewer = scope.querySelector('[data-detail-viewer]');
  if (!viewer || viewer.dataset.viewerReady) return;
  viewer.dataset.viewerReady = 'true';

  const root = viewer.closest('.product__detail') || document;
  const garment = viewer.querySelector('[data-viewer-garment]');
  const pane = viewer.querySelector('[data-viewer-pane]');
  const hint = viewer.querySelector('[data-viewer-hint]');
  const regionEls = [...viewer.querySelectorAll('.viewer__region')];
  const macros = [...pane.querySelectorAll('.viewer__macro')];
  const thumbs = [...root.querySelectorAll('.viewer__thumb')];

  let current = macros[0] ? macros[0].dataset.media : null;
  let rect = null;
  let running = false;

  /* Region geometry in normalised 0–1 space. Bands carry no x/y/r and are
     matched by vertical span instead of distance. */
  const regions = regionEls.map((el) => ({
    el,
    media: el.dataset.media,
    band: el.dataset.band !== undefined ? Number(el.dataset.band) : null,
    x: Number(el.dataset.x),
    y: Number(el.dataset.y),
    r: Number(el.dataset.r)
  }));

  function show(media) {
    if (!media || media === current) return;
    current = media;

    macros.forEach((m) => m.classList.toggle('is-active', m.dataset.media === media));
    thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.media === media));
    regionEls.forEach((el) => el.classList.toggle('is-active', el.dataset.media === media));
  }

  /** Nearest region centre wins, so overlapping regions resolve predictably. */
  function regionAt(nx, ny) {
    let best = null;
    let bestDistance = Infinity;

    for (const region of regions) {
      if (region.band !== null) {
        const top = region.band / 3;
        if (ny >= top && ny < top + 1 / 3) return region;
        continue;
      }
      const distance = Math.hypot(nx - region.x, ny - region.y);
      if (distance <= region.r && distance < bestDistance) {
        bestDistance = distance;
        best = region;
      }
    }
    return best;
  }

  /* The rect is cached rather than read per move: pointer events are already
     coalesced to roughly one per frame, so measuring here would only add a
     layout read to each. Deliberately no rAF — the swap should land on the
     same event that caused it, and a starved frame callback can leave the
     viewer stuck. */
  const measure = () => { rect = garment.getBoundingClientRect(); };

  function onMove(event) {
    if (!rect) measure();
    const region = regionAt(
      (event.clientX - rect.left) / rect.width,
      (event.clientY - rect.top) / rect.height
    );
    if (region) show(region.media);
    if (hint) hint.classList.add('is-hidden');
  }

  function onLeave() {
    regionEls.forEach((el) => el.classList.remove('is-active'));
    if (hint) hint.classList.remove('is-hidden');
  }

  function start() {
    if (running) return;
    running = true;
    measure();
    garment.addEventListener('pointermove', onMove, { passive: true });
    garment.addEventListener('pointerleave', onLeave);
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
  }

  function stop() {
    if (!running) return;
    running = false;
    garment.removeEventListener('pointermove', onMove);
    garment.removeEventListener('pointerleave', onLeave);
    window.removeEventListener('scroll', measure);
    window.removeEventListener('resize', measure);
    rect = null;
    onLeave();
  }

  const sync = () => {
    if (getMode() === 'immersive' && canBeImmersive()) start();
    else stop();
  };

  sync();
  document.addEventListener('ss:modechange', sync);

  /* Always on, in every mode: keyboard focus, clicks and the thumbnail strip.
     These keep every macro reachable when hover isn't available. */
  regionEls.forEach((el) => {
    el.addEventListener('focus', () => show(el.dataset.media));
    el.addEventListener('click', () => show(el.dataset.media));
  });

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => show(thumb.dataset.media));
  });
}

initDetailViewer();

/* The theme editor replaces section markup wholesale; without this the
   listeners would be left bound to detached nodes. */
document.addEventListener('shopify:section:load', (event) => initDetailViewer(event.target));
